import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 're-resizable';
import { motion, AnimatePresence } from 'framer-motion';
import { Logger, LogLevel, LogMessage, LogFilter, LoggerConfig } from '../../engine/core/Logger';
import { UIPerformance } from '../core/UIConfig';

interface LogModalProps {
    isVisible: boolean;
    onClose: () => void;
}

const LogModal: React.FC<LogModalProps> = ({ isVisible, onClose }) => {
    const [messages, setMessages] = useState<LogMessage[]>([]);
    const [filteredMessages, setFilteredMessages] = useState<LogMessage[]>([]);
    const [config, setConfig] = useState<LoggerConfig>(Logger.Instance.getConfig());
    const [filter, setFilter] = useState<LogFilter>({});
    const [searchText, setSearchText] = useState('');
    const [selectedLevels, setSelectedLevels] = useState<LogLevel[]>([LogLevel.Debug, LogLevel.Info, LogLevel.Warn, LogLevel.Error]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const [autoScroll, setAutoScroll] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [size, setSize] = useState({ width: 1000, height: 700 });
    const nodeRef = useRef<HTMLDivElement>(null);
    const logContainerRef = useRef<HTMLDivElement>(null);

    // Get UI performance configuration
    const uiConfig = UIPerformance.getConfig();

    // Initialize messages on mount
    useEffect(() => {
        if (!isVisible) return;
        
        const allMessages = Logger.Instance.getMessages();
        setMessages(allMessages);
        setAvailableCategories(Logger.Instance.getAvailableCategories());
    }, [isVisible]);

    // Update filtered messages when filters change
    useEffect(() => {
        const currentFilter: LogFilter = {
            levels: selectedLevels,
            categories: selectedCategories.length > 0 ? selectedCategories : undefined,
            searchText: searchText || undefined
        };
        
        const filtered = Logger.Instance.getMessages(currentFilter);
        setFilteredMessages(filtered);
    }, [messages, selectedLevels, selectedCategories, searchText]);

    // Subscribe to new messages (separate effect to avoid render-time setState)
    useEffect(() => {
        if (!isVisible) return;
        
        const handleNewMessage = () => {
            // Use setTimeout to defer the setState call until after render
            setTimeout(() => {
                const allMessages = Logger.Instance.getMessages();
                setMessages(allMessages);
                setAvailableCategories(Logger.Instance.getAvailableCategories());
            }, 0);
        };

        const unsubscribe = Logger.Instance.subscribe(handleNewMessage);
        return unsubscribe;
    }, [isVisible]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (autoScroll && logContainerRef.current) {
            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
        }
    }, [filteredMessages, autoScroll]);

    const handleConfigChange = (configUpdate: Partial<LoggerConfig>) => {
        const newConfig = { ...config, ...configUpdate };
        setConfig(newConfig);
        Logger.Instance.configure(newConfig);
    };

    const handleLevelToggle = (level: LogLevel) => {
        setSelectedLevels(prev => 
            prev.includes(level) 
                ? prev.filter(l => l !== level)
                : [...prev, level]
        );
    };

    const handleCategoryToggle = (category: string) => {
        setSelectedCategories(prev => 
            prev.includes(category) 
                ? prev.filter(c => c !== category)
                : [...prev, category]
        );
    };

    const clearLogs = () => {
        Logger.Instance.clearMessages();
        setMessages([]);
        setFilteredMessages([]);
    };

    const exportLogs = () => {
        const logText = filteredMessages
            .map(msg => `[${new Date(msg.timestamp).toISOString()}] [${msg.level}] ${msg.category ? `[${msg.category}] ` : ''}${msg.message}`)
            .join('\n');
            
        navigator.clipboard.writeText(logText).then(() => {
            //Logger.Instance.Info('Logs copied to clipboard', 'LogModal');
        }).catch(() => {
            // Fallback: download as file
            const blob = new Blob([logText], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `3d-presenter-logs-${new Date().toISOString().slice(0, 19)}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        });
    };

    const copyMessage = (msg: LogMessage) => {
        const logText = `[${new Date(msg.timestamp).toISOString()}] [${msg.level}] ${msg.category ? `[${msg.category}] ` : ''}${msg.message}`;
        navigator.clipboard.writeText(logText);
    };

    const getLevelColor = (level: LogLevel): string => {
        switch (level) {
            case LogLevel.Debug: return '#888';
            case LogLevel.Info: return '#0088ff';
            case LogLevel.Warn: return '#ff8800';
            case LogLevel.Error: return '#ff4444';
            default: return '#fff';
        }
    };

    const getLevelIcon = (level: LogLevel): string => {
        switch (level) {
            case LogLevel.Debug: return '🔍';
            case LogLevel.Info: return 'ℹ️';
            case LogLevel.Warn: return '⚠️';
            case LogLevel.Error: return '❌';
            default: return '📝';
        }
    };

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => {
                    // Don't stop propagation for slider elements - they need global cleanup
                    const target = e.target as HTMLElement;
                    if (!target.closest('[data-slider-element]')) {
                        e.stopPropagation();
                    }
                }}
                data-ui-element="log-modal"
            >
                <Draggable 
                    nodeRef={nodeRef as React.RefObject<HTMLElement>} 
                    handle=".log-drag-handle"
                    defaultPosition={{ x: 0, y: 0 }}
                    cancel=".no-drag"
                >
                    <div 
                        ref={nodeRef}
                        style={{ 
                            filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5))'
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerUp={(e) => {
                            // Don't stop propagation for slider elements - they need global cleanup
                            const target = e.target as HTMLElement;
                            if (!target.closest('[data-slider-element]')) {
                                e.stopPropagation();
                            }
                        }}
                        data-ui-element="log-modal-content"
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -20 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                            <Resizable
                                size={{ width: size.width, height: size.height }}
                                onResizeStop={(_e, _direction, _ref, d) => {
                                    setSize({
                                        width: Number(size.width) + d.width,
                                        height: Number(size.height) + d.height,
                                    });
                                }}
                                minWidth={600}
                                minHeight={400}
                                style={{
                                    background: `
                                        linear-gradient(135deg, 
                                            rgba(15, 15, 35, 0.95) 0%,
                                            rgba(25, 15, 45, 0.9) 25%,
                                            rgba(35, 25, 55, 0.9) 50%,
                                            rgba(25, 15, 45, 0.9) 75%,
                                            rgba(15, 15, 35, 0.95) 100%
                                        )
                                    `,
                                    backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(20px) saturate(180%)',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                {/* Cosmic glow effect - conditionally rendered for performance */}
                                {!uiConfig.disableCosmicBackgrounds && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-50%',
                                        left: '-50%',
                                        right: '-50%',
                                        bottom: '-50%',
                                        background: `
                                            radial-gradient(circle at 30% 20%, rgba(147, 112, 219, 0.15) 0%, transparent 50%),
                                            radial-gradient(circle at 70% 80%, rgba(75, 0, 130, 0.1) 0%, transparent 50%),
                                            radial-gradient(circle at 20% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
                                        `,
                                        pointerEvents: 'none',
                                        zIndex: -1,
                                    }} />
                                )}

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    color: 'white',
                                    fontFamily: "'Inter', 'SF Pro Display', system-ui, sans-serif",
                                }}>
                                    
                                    {/* Header */}
                                    <div 
                                        className="log-drag-handle"
                                        style={{
                                            cursor: 'move',
                                            padding: '24px 24px 8px 24px',
                                            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)',
                                            flexShrink: 0,
                                            position: 'relative',
                                        }}
                                    >
                                        {/* Drag indicator dots */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            display: 'flex',
                                            gap: '4px',
                                            pointerEvents: 'none',
                                            zIndex: 2,
                                        }}>
                                            {[1, 2, 3, 4, 5].map((i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        width: '3px',
                                                        height: '3px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                                    }}
                                                />
                                            ))}
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontSize: '24px' }}>🔬</span>
                                            <motion.h2 
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 }}
                                                style={{
                                                    margin: 0,
                                                    fontSize: '24px',
                                                    fontWeight: '700',
                                                    background: 'linear-gradient(135deg, #E6E6FA 0%, #DDA0DD 50%, #DA70D6 100%)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                }}
                                            >
                                                Advanced Log Viewer ({filteredMessages.length}/{messages.length})
                                            </motion.h2>
                                        </div>
                                        
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                className="no-drag"
                                                onClick={() => setIsCollapsed(!isCollapsed)}
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                                    color: '#fff',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    transition: 'all 0.2s ease',
                                                    backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                {isCollapsed ? '🔽' : '🔼'}
                                            </button>
                                            <button
                                                className="no-drag"
                                                onClick={onClose}
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(220, 38, 127, 0.2) 0%, rgba(220, 38, 127, 0.1) 100%)',
                                                    border: '1px solid rgba(220, 38, 127, 0.3)',
                                                    color: '#ff6b9d',
                                                    padding: '8px 12px',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    transition: 'all 0.2s ease',
                                                    backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 38, 127, 0.3) 0%, rgba(220, 38, 127, 0.15) 100%)';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 38, 127, 0.2) 0%, rgba(220, 38, 127, 0.1) 100%)';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </div>

                                    {!isCollapsed && (
                                        <>
                                            {/* Controls */}
                                            <div style={{
                                                padding: '20px 24px',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%)',
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '16px',
                                                alignItems: 'center',
                                            }}>
                                                {/* Search */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '500' }}>🔍 Search:</span>
                                                    <input
                                                        className="no-drag"
                                                        type="text"
                                                        value={searchText}
                                                        onChange={(e) => setSearchText(e.target.value)}
                                                        placeholder="Search messages..."
                                                        style={{
                                                            background: 'rgba(255, 255, 255, 0.05)',
                                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                                            color: '#fff',
                                                            padding: '8px 12px',
                                                            borderRadius: '8px',
                                                            fontSize: '13px',
                                                            width: '200px',
                                                            backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    />
                                                </div>

                                                {/* Level filters */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '500' }}>Levels:</span>
                                                    {Object.values(LogLevel).map(level => (
                                                        <label key={level} style={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: '6px', 
                                                            cursor: 'pointer',
                                                            padding: '4px 8px',
                                                            borderRadius: '6px',
                                                            background: selectedLevels.includes(level) 
                                                                ? 'rgba(255, 255, 255, 0.1)' 
                                                                : 'transparent',
                                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                                            transition: 'all 0.2s ease',
                                                        }}>
                                                            <input
                                                                className="no-drag"
                                                                type="checkbox"
                                                                checked={selectedLevels.includes(level)}
                                                                onChange={() => handleLevelToggle(level)}
                                                                style={{ margin: 0 }}
                                                            />
                                                            <span style={{ color: getLevelColor(level), fontSize: '12px', fontWeight: '500' }}>
                                                                {getLevelIcon(level)} {level}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>

                                                {/* Category filters */}
                                                {availableCategories.length > 0 && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                        <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '500' }}>Categories:</span>
                                                        {availableCategories.map(category => (
                                                            <label key={category} style={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: '6px', 
                                                                cursor: 'pointer',
                                                                padding: '4px 8px',
                                                                borderRadius: '6px',
                                                                background: selectedCategories.includes(category) 
                                                                    ? 'rgba(0, 136, 255, 0.2)' 
                                                                    : 'transparent',
                                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                                transition: 'all 0.2s ease',
                                                            }}>
                                                                <input
                                                                    className="no-drag"
                                                                    type="checkbox"
                                                                    checked={selectedCategories.includes(category)}
                                                                    onChange={() => handleCategoryToggle(category)}
                                                                    style={{ margin: 0 }}
                                                                />
                                                                <span style={{ color: '#0088ff', fontSize: '12px', fontWeight: '500' }}>{category}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Action buttons */}
                                                <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto', alignItems: 'center' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                                                        <input
                                                            className="no-drag"
                                                            type="checkbox"
                                                            checked={autoScroll}
                                                            onChange={(e) => setAutoScroll(e.target.checked)}
                                                            style={{ margin: 0 }}
                                                        />
                                                        Auto-scroll
                                                    </label>
                                                    <button
                                                        className="no-drag"
                                                        onClick={clearLogs}
                                                        style={{
                                                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                                            color: '#fff',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            transition: 'all 0.2s ease',
                                                            backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                        }}
                                                    >
                                                        🗑️ Clear
                                                    </button>
                                                    <button
                                                        className="no-drag"
                                                        onClick={exportLogs}
                                                        style={{
                                                            background: 'linear-gradient(135deg, rgba(0, 102, 204, 0.3) 0%, rgba(0, 102, 204, 0.15) 100%)',
                                                            border: '1px solid rgba(0, 102, 204, 0.4)',
                                                            color: '#4da6ff',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            transition: 'all 0.2s ease',
                                                            backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                        }}
                                                    >
                                                        📋 Export
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Config */}
                                            <div style={{
                                                padding: '12px 24px',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                                                background: 'rgba(255, 255, 255, 0.02)',
                                                display: 'flex',
                                                gap: '16px',
                                                alignItems: 'center',
                                                fontSize: '12px',
                                            }}>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: '500' }}>Config:</span>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.8)' }}>
                                                    <input
                                                        className="no-drag"
                                                        type="checkbox"
                                                        checked={config.showInConsole}
                                                        onChange={(e) => handleConfigChange({ showInConsole: e.target.checked })}
                                                    />
                                                    Console
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.8)' }}>
                                                    <input
                                                        className="no-drag"
                                                        type="checkbox"
                                                        checked={config.showInUI}
                                                        onChange={(e) => handleConfigChange({ showInUI: e.target.checked })}
                                                    />
                                                    UI
                                                </label>
                                                <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Max: {config.maxMessages}</span>
                                            </div>

                                            {/* Log messages */}
                                            <div
                                                ref={logContainerRef}
                                                style={{
                                                    flex: 1,
                                                    overflow: 'auto',
                                                    padding: '16px 24px',
                                                    background: 'rgba(0, 0, 0, 0.2)',
                                                }}
                                            >
                                                {filteredMessages.map((msg, index) => (
                                                    <motion.div
                                                        key={msg.id}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: Math.min(index * 0.02, 0.5) }}
                                                        onClick={() => copyMessage(msg)}
                                                        style={{
                                                            padding: '12px 16px',
                                                            marginBottom: '6px',
                                                            background: index % 2 === 0 
                                                                ? 'rgba(255, 255, 255, 0.03)' 
                                                                : 'rgba(255, 255, 255, 0.06)',
                                                            borderRadius: '8px',
                                                            borderLeft: `3px solid ${getLevelColor(msg.level)}`,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            fontSize: '12px',
                                                            fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
                                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                                            backdropFilter: uiConfig.disableBlurEffects ? 'none' : 'blur(10px)',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = uiConfig.disableGlowEffects 
                                                                ? 'none' 
                                                                : `0 4px 20px ${getLevelColor(msg.level)}20`;
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.background = index % 2 === 0 
                                                                ? 'rgba(255, 255, 255, 0.03)' 
                                                                : 'rgba(255, 255, 255, 0.06)';
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                                            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '11px' }}>
                                                                {new Date(msg.timestamp).toLocaleTimeString()}
                                                            </span>
                                                            <span style={{ 
                                                                color: getLevelColor(msg.level), 
                                                                fontWeight: '600',
                                                                fontSize: '11px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}>
                                                                {getLevelIcon(msg.level)} {msg.level}
                                                            </span>
                                                            {msg.category && (
                                                                <span style={{ 
                                                                    color: '#4da6ff', 
                                                                    fontWeight: '600',
                                                                    fontSize: '11px',
                                                                    background: 'rgba(77, 166, 255, 0.2)',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    border: '1px solid rgba(77, 166, 255, 0.3)'
                                                                }}>
                                                                    {msg.category}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ 
                                                            color: 'rgba(255, 255, 255, 0.9)', 
                                                            lineHeight: '1.5',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {msg.message}
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </Resizable>
                        </motion.div>
                    </div>
                </Draggable>
            </motion.div>
        </AnimatePresence>
    );
};

export default LogModal; 